import {
  useState,
  useRef,
  useMemo,
  RefObject,
  Dispatch,
  SetStateAction,
  useEffect,
  useCallback,
  MutableRefObject,
} from 'react'
import './TextEditor.css'
import { MentionComponent } from '@syncfusion/ej2-react-dropdowns'
import {
  ActionBeginEventArgs,
  RichTextEditorComponent,
} from '@syncfusion/ej2-react-richtexteditor'
import { useAppSelector } from '@store/hooks'
import { getFullName } from '@utils/fullname.util'

export interface IEditorInput {
  text: string
  htmlContent: string
}

export interface ITextEditorProps {
  handleAIFeaturesButtons: (command: string) => void
  handleClearBtn: () => void
  handleRightButtonsAction: (key: string) => void
  handleToolbarActions: (buttonName: string) => void
  isRecording: boolean
  mentionRef: RefObject<MentionComponent>
  onActionBegin: (e: ActionBeginEventArgs) => void
  onCreated: () => void
  onTextChange: () => void
  peopleList: { name: string }[]
  saveCaretPosition: () => void
  toolbarRef: RefObject<RichTextEditorComponent> | null
  variableRef: RefObject<MentionComponent>
}

const useTextEditor = (
  editorInput: IEditorInput,
  enableEnterNextLine: boolean,
  hideToolbar: boolean,
  isAiEnabled: boolean,
  setEditorInput: Dispatch<SetStateAction<IEditorInput>>,
  setIsAiEnabled: Dispatch<SetStateAction<boolean>>,
  handleSend: () => void,
  toolbarRef: RefObject<RichTextEditorComponent> | null,
  isEditing?: boolean,
  onSaveEdit?: () => void,
  setIsEditing?: Dispatch<SetStateAction<boolean>>,
  selectionRef?: MutableRefObject<Range | null>,
  persistAiMode?: boolean,
): ITextEditorProps => {
  const patients = useAppSelector((state) => state.patient.data)
  const [isRecording, setIsRecording] = useState<boolean>(false)
  const mentionRef = useRef<MentionComponent>(null)
  const variableRef = useRef<MentionComponent>(null)

  const peopleList = useMemo(() => {
    return patients.map((patient) => ({ name: getFullName(patient) }))
  }, [patients])

  const aiButtonRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!hideToolbar && toolbarRef?.current) {
      const toolbarEl = toolbarRef.current.getToolbar?.() ?? null
      aiButtonRef.current = toolbarEl?.querySelector('.ai') ?? null
    } else {
      aiButtonRef.current = null
    }
  }, [hideToolbar])

  useEffect(() => {
    if (aiButtonRef.current) {
      if (isAiEnabled) {
        aiButtonRef.current.classList.add('e-active')
      } else {
        aiButtonRef.current.classList.remove('e-active')
      }
    }
  }, [isAiEnabled])

  const handleToolbarActions = useCallback(
    (buttonName: string): void => {
      if (toolbarRef) {
        const ref = toolbarRef.current

        switch (buttonName) {
          case 'variable':
            {
              if (variableRef.current && ref) {
                if (ref.getHtml() === '<p><br></p>') ref.value = '<p>&nbsp;</p>'
                variableRef.current.showPopup()
              }
            }
            break
          case 'mention':
            if (mentionRef.current && ref) {
              if (ref.getHtml() === '<p><br></p>') ref.value = '<p>&nbsp;</p>'
              mentionRef.current.showPopup()
            }
            break
          case 'ai':
            {
              setIsAiEnabled((prevState) => !prevState)
            }
            break
          default:
            break
        }
      }
    },
    [toolbarRef, variableRef, mentionRef, setIsAiEnabled],
  )

  const onActionBegin = (e: ActionBeginEventArgs): void => {
    if (e.requestType === 'EnterAction') {
      if (enableEnterNextLine) {
        e.cancel = false
      } else {
        e.cancel = true
        toolbarRef?.current?.focusOut()
      }
    }
  }

  const handleClearBtn = (): void => {
    setEditorInput({
      text: '',
      htmlContent: '',
    })
    if (isAiEnabled && !persistAiMode) setIsAiEnabled(false)
    if (isEditing && setIsEditing) setIsEditing(false)
  }

  const handleRecordAudio = (): void => {
    setIsRecording(!isRecording)
  }

  const handleRightButtonsAction = (key: string): void => {
    switch (key) {
      case 'send':
        {
          handleSend()
          if (isEditing && onSaveEdit) {
            onSaveEdit()
          }
        }
        break
      case 'mic':
        handleRecordAudio()
        break
      default:
        break
    }
  }

  const handleAIFeaturesButtons = (command: string): void => {
    let newInputPrompt = ''
    switch (command.toLowerCase()) {
      case 'reply':
        newInputPrompt = 'Generate a response for the following message:\n '
        break
      case 'summarize':
        newInputPrompt = `Summarize the message below. Make 
          sure to retain the same context with shorter number of characters.\n `
        break
    }

    if (toolbarRef?.current) toolbarRef.current.value = newInputPrompt
    setEditorInput({
      text: newInputPrompt,
      htmlContent: newInputPrompt,
    })
  }

  const saveCaretPosition = (): void => {
    const rte = toolbarRef?.current
    if (!rte || !selectionRef) return
    const editPanel = rte.contentModule?.getEditPanel?.() as
      | HTMLElement
      | undefined
    if (!editPanel) return
    const doc = editPanel.ownerDocument
    const win = doc.defaultView ?? window
    try {
      const sel = win.getSelection()
      if (sel?.rangeCount) {
        const r = sel.getRangeAt(0)
        if (editPanel.contains(r.commonAncestorContainer)) {
          selectionRef.current = r.cloneRange()
          return
        }
      }
    } catch {
      /* */
    }
    const nodeSelection = rte.formatter?.editorManager?.nodeSelection
    if (!nodeSelection) return
    try {
      const range = nodeSelection.getRange(doc)
      if (range && editPanel.contains(range.commonAncestorContainer)) {
        selectionRef.current = range.cloneRange()
      }
    } catch {
      /* range not available */
    }
  }

  const onTextChange = (): void => {
    if (toolbarRef?.current)
      setEditorInput({
        text: toolbarRef.current.getText(),
        htmlContent: toolbarRef.current.getHtml(),
      })
    saveCaretPosition()
  }

  const onCreated = (): void => {
    if (toolbarRef?.current) {
      const editorElement =
        toolbarRef.current.element?.querySelector('.e-content')
      if (editorElement) {
        editorElement.addEventListener('input', onTextChange)
        editorElement.addEventListener('paste', () =>
          setTimeout(onTextChange, 0),
        )
        editorElement.addEventListener('drop', () =>
          setTimeout(onTextChange, 0),
        )
      }
    }
  }

  const handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  useEffect(() => {
    return () => {
      if (toolbarRef?.current) {
        const editorElement =
          toolbarRef.current.element?.querySelector('.e-content')
        if (editorElement) {
          editorElement.removeEventListener('input', onTextChange)
          editorElement.removeEventListener('paste', () =>
            setTimeout(onTextChange, 0),
          )
          editorElement.removeEventListener('drop', () =>
            setTimeout(onTextChange, 0),
          )
        }
      }
    }
  }, [])

  useEffect(() => {
    if (isEditing) {
      if (toolbarRef?.current) toolbarRef.current.value = editorInput.text
    }
  }, [isEditing, editorInput])

  useEffect(() => {
    const reference = toolbarRef?.current
    let refElement: Element | undefined = undefined

    if (reference && reference.contentModule) {
      refElement = reference.contentModule.getEditPanel?.()
      if (refElement) {
        refElement.addEventListener('keydown', handleKeyDown as EventListener)

        const editorRoot = reference.element
        if (editorRoot) {
          editorRoot.addEventListener('keydown', handleKeyDown as EventListener)
        }
      }
    }

    return () => {
      if (refElement) {
        refElement.removeEventListener(
          'keydown',
          handleKeyDown as EventListener,
        )
        const editorRoot = reference?.element
        if (editorRoot) {
          editorRoot.removeEventListener(
            'keydown',
            handleKeyDown as EventListener,
          )
        }
      }
    }
  }, [handleSend])

  return {
    handleAIFeaturesButtons,
    handleClearBtn,
    handleRightButtonsAction,
    handleToolbarActions,
    isRecording,
    mentionRef,
    onActionBegin,
    onCreated,
    onTextChange,
    peopleList,
    saveCaretPosition,
    toolbarRef,
    variableRef,
  }
}

export default useTextEditor
